"use client";

import type { ReactNode } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Eye, Info } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ComposeFormValues } from "@/lib/types/compose-modal-types";
import { COMPOSE_TRACK_OPENS_KEY } from "@/lib/hooks/compose/useComposeFormState";

interface ComposeTrackOpensToggleProps {
  form: UseFormReturn<ComposeFormValues>;
}

/**
 * Per-email "track opens" opt-in. Bound to the compose form's `trackOpens`
 * field; toggling it also remembers the choice per device as the default for the
 * next compose (see COMPOSE_TRACK_OPENS_KEY). Off by default — 1:1 open pixels
 * are opt-in for deliverability/privacy reasons.
 */
export function ComposeTrackOpensToggle({ form }: ComposeTrackOpensToggleProps): ReactNode {
  const checked = form.watch("trackOpens") ?? false;

  const handleChange = (next: boolean): void => {
    form.setValue("trackOpens", next, { shouldDirty: true });
    try {
      window.localStorage.setItem(COMPOSE_TRACK_OPENS_KEY, next ? "true" : "false");
    } catch {
      // Non-fatal: the send still honors the in-form value.
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id="compose-track-opens"
        checked={checked}
        onCheckedChange={(value) => handleChange(value === true)}
      />
      <Label
        htmlFor="compose-track-opens"
        className="flex items-center gap-1.5 text-sm font-normal text-muted-foreground cursor-pointer"
      >
        <Eye className="h-3.5 w-3.5" />
        Track when opened
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex text-muted-foreground/70 hover:text-muted-foreground"
              aria-label="How open tracking works"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs leading-relaxed">
            Adds an invisible 1×1 pixel to this email. When the recipient&apos;s
            mail app loads images, we record an open and show an &ldquo;Opened&rdquo;
            badge on the communication. Opens are an approximate signal — many
            clients (notably Apple Mail Privacy Protection) preload images, which
            can mark an email opened even if unread, and clients that block images
            never report an open.
          </TooltipContent>
        </Tooltip>
      </Label>
    </div>
  );
}
